import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FieldProps, FormControl, useFormField } from '@core/utils/form';
import MFormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import { components } from 'react-select';
import AsyncSelect from 'react-select/async';
import { Label } from 'reactstrap';
import { tap } from 'rxjs/operators';
import { R } from '@core/utils/r';
import { Theme } from '@core/utils/css';
import { GoogleMapsAutocompleteResult } from '@business/entities/integration';
import { GoogleMapsAPI } from '@business/api/google_maps';

const lightCustomStyles = {
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
};

const darkCustomStyles = {
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  singleValue: (base: any) => ({ ...base, color: '#bfc8e2' }),
  control: (base: any) => ({ ...base, backgroundColor: '#111', borderColor: '#2a2a2a' }),
  menu: (base: any) => ({ ...base, backgroundColor: '#111', border: '1px solid #2a2a2a' }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected ? '#319cf4' : state.isFocused ? '#deebff20' : '#111',
    color: '#FFF',
  }),
};

const DropdownIndicator = (props: any) => (
  <components.DropdownIndicator {...props}>
    <i className="fa fa-search" />
  </components.DropdownIndicator>
);

export interface FormPlaceAutoCompleteProps extends FieldProps<FormControl<string>> {
  onSelect?(result: GoogleMapsAutocompleteResult): void;
  withoutGroup?: boolean;
  asterisk?: boolean;
  disabled?: boolean;
  debounce?: number;
}

export function FormPlaceAutoComplete(props: FormPlaceAutoCompleteProps) {
  const { formControl, fieldState, fieldConfig } = useFormField(props);
  const { debounce = 400 } = props;

  const fieldTouched = (fieldState && fieldState.touched) || formControl.inheritedSubmitted || false;
  const fieldError = (fieldTouched && fieldState.errorMessage) || null;

  const [selected, setSelected] = useState<{ value: string; label: string } | null>(null);

  useEffect(() => {
    setSelected(fieldState.value ? { value: fieldState.value, label: fieldState.value } : null);
  }, [fieldState.value]);

  const loadOptions = useCallback((input: string, callback: (options: any[]) => void) => {
    if (!input || input.length < 2) {
      callback([]);
      return;
    }

    const customOption = {
      value: input,
      label: input,
      data: { isCustom: true, description: input, placeId: '' } as GoogleMapsAutocompleteResult & { isCustom: boolean },
    };

    GoogleMapsAPI.getAutocomplete(input)
      .pipe(
        tap(results => {
          callback([
            customOption,
            ...results.map(r => ({
              value: r.description,
              label: r.description,
              data: r,
            })),
          ]);
        }),
      )
      .subscribe();
  }, []);

  const debouncedLoadOptions = useMemo(() => R.debounce(loadOptions, debounce), [loadOptions, debounce]);

  const content = (
    <React.Fragment>
      <Label>
        {fieldConfig.label} {fieldConfig.required && props.asterisk && <span className="text-danger">*</span>}
      </Label>
      <MFormControl error={!!fieldError} fullWidth>
        <AsyncSelect
          cacheOptions
          isClearable
          loadOptions={debouncedLoadOptions}
          value={selected}
          styles={Theme.isDark ? darkCustomStyles : lightCustomStyles}
          isDisabled={props.disabled}
          placeholder={fieldConfig.placeholder || 'Search address...'}
          onFocus={() => formControl.onFocus()}
          onBlur={() => formControl.onBlur()}
          onChange={option => {
            if (!option) {
              formControl.setValue(null);
              setSelected(null);
              return;
            }

            const result = option.data as GoogleMapsAutocompleteResult & { isCustom?: boolean };
            formControl.setValue(result.description);
            setSelected({ value: result.description, label: result.description });
            if (!result.isCustom) {
              props.onSelect && props.onSelect(result);
            }
          }}
          components={{ DropdownIndicator }}
          menuPortalTarget={document.body}
          classNamePrefix="select2-selection"
          className={Theme.isDark ? 'select2-selection' : undefined}
          noOptionsMessage={({ inputValue }) => (!inputValue ? 'Start typing to search...' : 'No results found')}
        />
        {!!fieldError && <FormHelperText className="mui-error">{fieldError}</FormHelperText>}
      </MFormControl>
    </React.Fragment>
  );

  if (props.withoutGroup) return content;
  return <div className="form-group">{content}</div>;
}
